import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { GlobalNavbar } from '../components/global-navbar';
import { blogPosts } from '../content/blogPosts';
import { useSeo } from '../utils/useSeo';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((entry) => entry.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const seoTitle = post ? `${post.title} | Profit Cars Blog` : 'Profit Cars Blog | Insights for Car Dealers';
  const seoDescription =
    post?.description ||
    'Read Profit Cars insights for EU car dealers: sourcing strategy, AI listing workflows, and margin optimization.';
  const seoPath = post ? `/blog/${post.slug}` : '/blog';

  useSeo({
    description: seoDescription,
    image: post?.image,
    keywords: [
      'car dealer insights',
      'used car market',
      'car dealership operations',
      'profit cars blog',
      post?.title || 'car dealer article',
    ],
    path: seoPath,
    structuredData: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          author: {
            '@type': 'Organization',
            name: 'Profit Cars Editorial Team',
          },
          datePublished: post.publishedAtIso,
          description: post.description,
          headline: post.title,
          image: post.image,
          keywords: post.title,
          mainEntityOfPage: `/blog/${post.slug}`,
          publisher: {
            '@type': 'Organization',
            logo: {
              '@type': 'ImageObject',
              url: '/logo.png',
            },
            name: 'Profit Cars',
          },
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'Blog',
          description: seoDescription,
          name: 'Profit Cars Blog',
          url: '/blog',
        },
    title: seoTitle,
    type: post ? 'article' : 'website',
  });

  if (!post) {
    return <Navigate replace to="/" />;
  }

  const relatedPosts = blogPosts.filter((entry) => entry.slug !== post.slug).slice(0, 2);

  return (
    <main className="min-h-screen bg-[#ececec] text-[#111827]">
      <GlobalNavbar />

      <section className="border-b border-black/10 bg-[#e6e7ea]">
        <div className="mx-auto w-full max-w-6xl px-6 pb-12 pt-12 lg:px-8">
          <article className="max-w-4xl">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#2f5b1b]">Profit Cars Insights</p>
            <h1 className="mt-4 font-display text-5xl leading-[0.95] text-black sm:text-6xl">{post.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-semibold text-slate-500">
              <span>{post.publishedAt}</span>
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              <span>{post.readingTime}</span>
            </div>
          </article>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <div className="overflow-hidden rounded-[28px] border border-black/10 bg-white">
            <img alt={post.title} className="aspect-[16/8] w-full object-cover" src={post.image} />
          </div>

          <article className="mx-auto mt-12 max-w-4xl">
            <p className="text-lg leading-relaxed text-slate-700">{post.description}</p>

            <div className="mt-10 space-y-10">
              {post.sections.map((section) => (
                <section key={section.title}>
                  <h2 className="font-display text-3xl leading-tight text-black sm:text-4xl">{section.title}</h2>
                  <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-700">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>

                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-5 space-y-3 text-base leading-relaxed text-slate-700">
                      {section.bullets.map((item) => (
                        <li className="flex items-start gap-3" key={item}>
                          <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#53b12c]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="border-t border-black/10 py-16 sm:py-20">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="font-display text-4xl leading-tight text-black sm:text-5xl">More insights</h2>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {relatedPosts.map((entry) => (
              <Link
                className="group overflow-hidden rounded-[24px] border border-black/10 bg-white transition hover:border-black/25"
                key={entry.slug}
                to={`/blog/${entry.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-black/5">
                  <img
                    alt={entry.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                    src={entry.image}
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-extrabold leading-tight text-black">{entry.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{entry.description}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.09em] text-[#2f5b1b]">
                    Read article
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              className="inline-flex h-12 items-center justify-center rounded-[10px] border border-black/65 bg-[#08090c] px-8 text-sm font-bold uppercase tracking-[0.06em] text-white transition hover:border-white/35 hover:bg-[#141820]"
              to="/blog"
            >
              See all blog posts
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
